import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CircleHalfTilt,
  Lifebuoy,
  List,
  MagnifyingGlass,
  Moon,
  Sparkle,
  Sun,
  PaperPlaneRight,
} from "@phosphor-icons/react";

import { searchAll, sendSupportMessage } from "@/lib/api";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const THEME_KEY = "uteflow-theme";

function applyTheme(theme) {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", theme === "dark");
}

export default function Topbar({ currentUser, billingInfo, onOpenSidebar }) {
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return window.localStorage.getItem(THEME_KEY) || "light";
  });
  const [helpOpen, setHelpOpen] = useState(false);
  const [supportMessage, setSupportMessage] = useState("");
  const [sendingSupport, setSendingSupport] = useState(false);

  const handleSendSupport = async (e) => {
    e.preventDefault();
    if (!supportMessage.trim()) return;
    setSendingSupport(true);
    try {
      await sendSupportMessage(supportMessage);
      toast.success("Message sent! Our team will get back to you shortly.");
      setSupportMessage("");
      setHelpOpen(false);
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSendingSupport(false);
    }
  };

  const showTrialCta = billingInfo?.subscription_status === "trial" && billingInfo?.is_active;
  const trialLabel = useMemo(() => {
    const daysLeft = billingInfo?.days_left;
    if (typeof daysLeft === "number" && daysLeft > 0) {
      return `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`;
    }
    return "Trial active";
  }, [billingInfo?.days_left]);

  useEffect(() => {
    applyTheme(theme);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(THEME_KEY, theme);
    }
  }, [theme]);

  const handleSearch = async (q) => {
    setQuery(q);
    if (q.trim().length < 2) {
      setResults(null);
      setShowResults(false);
      setIsSearching(false);
      return;
    }
    try {
      setIsSearching(true);
      setShowResults(true);
      const data = await searchAll(q);
      setResults(data);
      setShowResults(true);
    } catch {
      setResults(null);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    setShowResults(false);
    setResults(null);
    setQuery("");
    setIsSearching(false);
  }, [location.pathname]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  const handleResultNavigation = (path) => {
    navigate(path);
    setShowResults(false);
    setResults(null);
    setQuery("");
  };

  return (
    <>
      <header
        className="flex flex-wrap items-center gap-3 border-b border-border/80 bg-white/90 px-4 py-3 backdrop-blur-xl dark:border-white/10 dark:bg-neutral-950/90 sm:gap-4 sm:px-6"
        data-testid="topbar"
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="mobile-nav-trigger"
            onClick={onOpenSidebar}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-white text-muted-foreground hover:bg-foreground/5 dark:border-white/10 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.06] lg:hidden"
            aria-label="Open navigation menu"
          >
            <List weight="bold" className="w-5 h-5" />
          </button>
        </div>

        <div ref={containerRef} className="order-3 basis-full min-w-0 sm:order-2 sm:flex-1">
          <div className="relative w-full">
            <MagnifyingGlass weight="bold" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70 dark:text-neutral-500" />
            <input
              data-testid="topbar-search"
              type="text"
              placeholder="Search rentals, vehicles, customers..."
              className="w-full rounded-2xl border border-border bg-white/95 py-2.5 pl-10 pr-4 text-sm font-medium shadow-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black/10 dark:border-white/10 dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-500 dark:focus:border-white dark:focus:ring-white/10"
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              onBlur={() => setTimeout(() => setShowResults(false), 350)}
              onFocus={() => results && setShowResults(true)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setShowResults(false);
                }
              }}
            />
            {showResults && (results || isSearching) && (
              <div
                data-testid="search-results"
                className="absolute left-0 right-0 top-full z-50 mt-2 max-h-80 overflow-y-auto rounded-2xl border border-border bg-white shadow-[0_18px_60px_rgba(0,0,0,0.08)] dark:border-white/10 dark:bg-neutral-950 dark:shadow-[0_18px_60px_rgba(0,0,0,0.35)]"
              >
                {isSearching && (
                  <div className="p-4 text-center text-sm text-muted-foreground dark:text-neutral-400">Searching…</div>
                )}
                {!isSearching && results && (
                  <>
                    {results.rentals?.length > 0 && (
                      <div className="p-2">
                        <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70 dark:text-neutral-500">Rentals</p>
                        {results.rentals.map((r) => (
                          <button
                            key={r.id}
                            onClick={() => handleResultNavigation(`/rentals/${r.id}`)}
                            className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-foreground/5 dark:hover:bg-white/[0.06]"
                          >
                            <span className="font-mono font-bold text-foreground dark:text-white">{r.rental_number}</span>
                            <span className="truncate text-muted-foreground dark:text-neutral-400">{r.customer_name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {results.vehicles?.length > 0 && (
                      <div className="border-t border-border/60 p-2 dark:border-white/10">
                        <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70 dark:text-neutral-500">Vehicles</p>
                        {results.vehicles.map((v) => (
                          <button
                            key={v.id}
                            onClick={() => handleResultNavigation("/fleet")}
                            className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-foreground/5 dark:hover:bg-white/[0.06]"
                          >
                            <span className="font-mono font-bold text-foreground/80 dark:text-neutral-200">{v.registration}</span>
                            <span className="text-muted-foreground dark:text-neutral-400">{v.make} {v.model}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {results.customers?.length > 0 && (
                      <div className="border-t border-border/60 p-2 dark:border-white/10">
                        <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70 dark:text-neutral-500">Customers</p>
                        {results.customers.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => handleResultNavigation(`/rentals?customer=${encodeURIComponent(c.full_name)}`)}
                            className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-foreground/5 dark:hover:bg-white/[0.06]"
                          >
                            <span className="font-semibold text-foreground dark:text-white">{c.full_name}</span>
                            <span className="text-muted-foreground dark:text-neutral-400">{c.mobile}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {!results.rentals?.length && !results.vehicles?.length && !results.customers?.length && (
                      <div className="p-4 text-center text-sm text-muted-foreground dark:text-neutral-400">No results found</div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="order-2 ml-auto flex items-center gap-2 sm:order-3">
          {showTrialCta && (
            <button
              type="button"
              data-testid="trial-topbar-btn"
              onClick={() => navigate("/billing")}
              className="hidden items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-bold text-amber-700 shadow-sm transition-colors hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300 md:inline-flex"
            >
              <Sparkle weight="bold" className="h-4 w-4" />
              <span>Activate plan</span>
              <span className="rounded-full bg-amber-200/80 px-2.5 py-1 text-xs text-amber-800 dark:bg-amber-300/15 dark:text-amber-200">
                {trialLabel}
              </span>
            </button>
          )}

          <button
            type="button"
            data-testid="topbar-help-trigger"
            onClick={() => setHelpOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-white text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground dark:border-white/10 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.06] dark:hover:text-white"
            aria-label="Open help panel"
          >
            <Lifebuoy weight="duotone" className="w-5 h-5" />
          </button>

          <button
            type="button"
            data-testid="topbar-theme-toggle"
            onClick={() => setTheme((current) => (current === "dark" ? "light" : "dark"))}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-white text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground dark:border-white/10 dark:bg-neutral-900 dark:text-neutral-300 dark:hover:bg-white/[0.06] dark:hover:text-white"
            aria-label="Toggle dark mode"
          >
            {theme === "dark" ? <Sun weight="duotone" className="w-5 h-5" /> : <Moon weight="duotone" className="w-5 h-5" />}
          </button>

          <button
            type="button"
            data-testid="topbar-profile-trigger"
            onClick={() => navigate("/profile")}
            className="flex items-center gap-2 rounded-2xl border border-border bg-white px-2 py-1.5 transition-colors hover:bg-foreground/5 dark:border-white/10 dark:bg-neutral-900 dark:hover:bg-white/[0.06]"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-black text-xs font-bold text-white dark:bg-white dark:text-foreground">
              {currentUser?.full_name?.charAt(0) || "U"}
            </div>
            <span className="hidden max-w-[8rem] truncate pr-1 text-sm font-semibold text-slate-700 dark:text-neutral-200 sm:block">
              {currentUser?.full_name || "Profile"}
            </span>
          </button>
        </div>
      </header>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Contact Support</DialogTitle>
            <DialogDescription>
              Got a question, feature request, or found a bug? Send us a message and our team will get back to you.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSendSupport} className="mt-4 space-y-4">
            <textarea
              required
              rows={5}
              placeholder="How can we help?"
              className="w-full resize-none rounded-xl border border-border bg-white px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:bg-neutral-950 dark:border-white/10 dark:text-neutral-200"
              value={supportMessage}
              onChange={(e) => setSupportMessage(e.target.value)}
            />
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={sendingSupport || !supportMessage.trim()}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {sendingSupport ? "Sending..." : "Send Message"}
                {!sendingSupport && <PaperPlaneRight weight="bold" className="h-4 w-4" />}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
